import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import moment from "moment";
import DetallePeriodoCobro from "./DetallePeriodoCobro";
import {
  getDetalleCobroByIdPeriodoFacturacion,
  marcarEnvioAsignacion,
  getEnviadoValueById,
  actualizarMediciones,
  guardarDetalleDocumento,
  getDetallePeriodo,
} from "../../../services/CierreService.js";

const CierreTable = ({
  asignaciones,
  filters,
  loading,
  actionBodyTemplate,
  fila,
  setFila,
  displayBasic,
  setDisplayBasic,
  baseUrl,
  urlMedidores,
  urlDocumentos,
  monedaOptions,
  valorUfUsada,
  isMonedaServiceUnavailable,
  buttonTemplate,
  handleFetchAllData,
  selectedEmpresa,
  setLoading,
  primerDiaHabil,
  fechaVencimientoHabil,
  periodoLetras,
  empresasMap,
  cabeceraMap,
  previousPeriodo,
  periodoDeCobro,
  toast,
}) => {
  const [first, setFirst] = useState(0);
  const [tableKey, setTableKey] = useState(0);
  const [detallesCobro, setDetallesCobro] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [summary, setSummary] = useState({});
  const [error, setError] = useState(null);
  const [globalFilter, setGlobalFilter] = useState('');
  const today = moment();
  const [exportLoading, setExportLoading] = React.useState(false);
  const fechaDeEmision = today.format("DD/MM/YYYY");
  const vencimiento = moment(periodoDeCobro)
    .locale("es")
    .date(25)
    .format("DD/MM/YYYY");
  const ufItem = monedaOptions?.find(
    (moneda) => moneda?.descripcion === "Unidad de Fomento"
  );
  const ufTipoCambio =
    Number.isFinite(Number(valorUfUsada)) && Number(valorUfUsada) > 0
      ? Number(valorUfUsada)
      : ufItem
        ? ufItem?.tipoCambio
        : 0;
  const tieneValorUf =
    Number.isFinite(Number(ufTipoCambio)) && ufTipoCambio > 0;

  useEffect(() => {
    setFirst(0);
    setTableKey((prevKey) => prevKey + 1);
  }, [selectedEmpresa]);

  const esAsignacionPadre = (rowData) =>
    asignaciones?.some(
      (a) => a?.folio && a.folio.startsWith(`${rowData?.idCotizacion}-`)
    );

  const handleOpenDialog = async (rowData) => {
    setFila(rowData);
    setDisplayBasic(true);
    setModalLoading(true);
    setError(null);
    try {
      const data = await getDetalleCobroByIdPeriodoFacturacion(baseUrl, rowData.id);
      if (data.respuesta) {
        setDetallesCobro(data.detallesCobro);
        calculateSummary(data.detallesCobro, monedaOptions);
      } else {
        setDetallesCobro([]);
        setSummary({});
        setError("No data found.");
      }
    } catch (error) {
      console.error("Error fetching data", error);
      setDetallesCobro([]);
      setSummary({});
      setError(error.message);
    }
    setModalLoading(false);
  };

  const formatNumber = (amount) => {
    return new Intl.NumberFormat("es-CL", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const calculateSummary = (detalles, monedaOptions) => {
    const summary = {
      total: 0, // Sum of all amounts in UF
      totalClp: 0, // Sum of all amounts converted to CLP
      arriendoTotal: 0, // Sum of all amounts converted to CLP
      gastoComunTotal: 0, // Sum of all amounts converted to CLP
      serviciosTotal: 0, // Sum of all amounts converted to CLP
      obrasCivilesTotal: 0, // Sum of all amounts converted to CLP
    };

    detalles.forEach((detalle) => {
      let monedaUf = monedaOptions.find((m) => m.codigo === "UF");
      let moneda = monedaOptions.find((m) => m.codigo === detalle.moneda);

      if (moneda) {
        let amountInUF =
          moneda.codigo === "UF"
            ? detalle.total
            : detalle.total / monedaUf.tipoCambio;

        let amountInCLP = detalle.total * moneda.tipoCambio.toFixed(2);

        if (detalle.servicios === "arriendo") {
          summary.arriendoTotal += amountInUF;
        } else if (detalle.servicios === "gasto comun") {
          summary.gastoComunTotal += amountInUF;
        } else if (detalle.servicios === "servicio") {
          summary.serviciosTotal += amountInUF;
        } else if (detalle.servicios === "obra civil") {
          summary.obrasCivilesTotal += amountInUF;
        }

        summary.total += amountInUF;
        summary.totalClp += amountInCLP;
      }
    });
    setSummary(summary);
  };

  //detalles de cotizacion
  const arriendoDescriptions = detallesCobro
    .filter(
      (item) => item?.servicios === "arriendo" && !item?.item.includes(" - ")
    ) // Filter items where servicios is "arriendo"
    .map((item) => item?.item) // Map to get the descripcion
    .join(" / "); // Join descriptions with a comma and space
  const arriendoItems = detallesCobro.filter(
    (item) => item.servicios === "arriendo"
  );
  const gastoComunItems = detallesCobro.filter(
    (item) => item.servicios === "gasto comun"
  );
  const serviciosItems = detallesCobro.filter(
    (item) => item.servicios === "servicio"
  );
  const obrasCivilesItems = detallesCobro.filter(
    (item) => item.servicios === "obra civil"
  );
  const basicDialogFooter = (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Button
        type="button"
        label="Cerrar"
        onClick={() => setDisplayBasic(false)}
        icon="pi pi-times"
        className="p-button-rounded p-button-outlined mr-2 mb-2 mt-2"
        autoFocus
      />
    </div>
  );

  const exportToPDF = async () => {
    setExportLoading(true);
    const input = document.getElementById("dialog-content");
    if (!input) {
      console.error("Element with ID 'dialog-content' not found");
      return;
    }

    // Cargar logo desde public/logoCBQ.png (ruta segura con PUBLIC_URL)
    const logoUrl = `${process.env.PUBLIC_URL}/logoCBQ.png`;

    html2canvas(input, { scale: 2 })
      .then(async (canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        const imgWidth = 190; // A4 width minus margins (210 - 20)
        const pageHeight = 297;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 30; // deja espacio para el logo

        // Agregar logo PNG al PDF (cargado desde public)
        try {
          const resp = await fetch(logoUrl);
          const blob = await resp.blob();
          const logoDataUrl = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
          pdf.addImage(logoDataUrl, "PNG", 10, 10, 40, 20); // ajusta tamaño/posición si necesitas
        } catch (e) {
          console.warn("No se pudo cargar el logo desde public/logoCBQ.png", e);
        }

        // Contenido capturado con html2canvas
        pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
        heightLeft -= pageHeight - 30;

        while (heightLeft >= 0) {
          position = heightLeft - imgHeight + 10;
          pdf.addPage();
          pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
          heightLeft -= pageHeight - 30;
        }

        const pdfData = pdf.output("bloburl");
        window.open(pdfData);
      })
      .catch((error) => {
        console.error("Error generating PDF:", error);
      });
  };

  const bankDetailsByEmpresa = {
    ALTAMIRA: `
  Banco: ITAU
  Rut: 76.071.007-5
  Nombre: ALTAMIRA SA
  Tipo de Cuenta: Corriente
  N° Cuenta: 2120941106
  `,
    "LA SIEMBRA": `
  Banco: ITAU
  Rut: 76.066.122-8
  Nombre: SOC AGRIC E INMOBILIARIA LA SIEMBRA LTDA
  Tipo de Cuenta: Corriente
  N° Cuenta: 223286902
  `,
  };

  const handleCopyDetails = () => {
    const detailsToCopy = bankDetailsByEmpresa[fila?.empresa];
    if (!detailsToCopy) {
      alert(`No hay datos bancarios configurados para la empresa "${fila?.empresa || "desconocida"}".`);
      return;
    }
    navigator.clipboard.writeText(detailsToCopy).then(() => {
      alert("Detalles copiados al portapapeles");
    });
  };

  const postMediciones = async (consumoMedidores) => {
    try {
      await actualizarMediciones(
        baseUrl,
        { periodo: previousPeriodo, empresa: selectedEmpresa, valorUf: ufTipoCambio },
        consumoMedidores
      );
      await handleFetchAllData();
    } catch (error) {
      console.error("Error:", error);
      throw error;
    }
  };

  const handleSincronizar = async () => {
    setLoading(true);
    try {
      const response = await getDetallePeriodo(urlMedidores, previousPeriodo);

      // si no hay mediciones, avisar y cortar
      const detalles = Array.isArray(response?.detalles) ? response.detalles : [];
      if (detalles.length === 0) {
        toast.current.show({ severity: 'warn', summary: 'Advertencia', detail: 'No se encontraron mediciones para el período seleccionado.', life: 3000 });
        setLoading(false);
        return;
      }
      
      await postMediciones(detalles);
      toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Sincronización completada correctamente', life: 3000 });
      setLoading(false);
    } catch (error) {
      console.error("Error:", error);
      toast.current.show({ severity: 'error', summary: 'Error', detail: 'Ocurrió un error al sincronizar', life: 3000 });
      setLoading(false);
    }
  };

  const sendDataToSystem = async (rowData) => {
    if (!Number.isFinite(Number(ufTipoCambio)) || !primerDiaHabil || !fechaVencimientoHabil) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "No se pudieron determinar las fechas hábiles o el valor UF del período. Reintenta más tarde.",
        life: 5000,
      });
      return;
    }
    if (esAsignacionPadre(rowData)) {
      alert("Esta asignación es un padre histórico, solo referencia.");
      return;
    }
    // console.log("Contenido de rowData:", rowData);
    if (rowData.enviado !== 0) {
      alert(`Esta asignación ya fue enviada a facturador.`);
      return;
    }
    try {
      setLoading(true);

      const dataEnviado = await getEnviadoValueById(baseUrl, rowData.id);
      if (!dataEnviado.respuesta) {
        alert(`Error: ${dataEnviado.mensaje}`);
        return;
      }

      if (dataEnviado.enviado !== 0) {
        alert(`Esta asignación ya fue enviada a facturador.`);
        return;
      }

      if (dataEnviado.ejecutado !== 1) {
        alert("El cierre de esta asignación no ha sido ejecutado. Ejecute el cierre antes de enviar.");
        return;
      }

      if (!rowData.cerrada) {
        const userConfirmed = window.confirm(
          "Esta asignación no tiene valores en sus mediciones. ¿Desea enviarla igualmente?"
        );

        if (!userConfirmed) {
          return;
        }
      }

      const data = await getDetalleCobroByIdPeriodoFacturacion(baseUrl, rowData.id);

      // Resolver la empresa (rut) y la cabecera de cierre correspondiente a la asignación
      const empresaAsignacion = empresasMap?.[rowData.empresa];
      if (!empresaAsignacion?.rut) {
        alert(`No se encontró la empresa "${rowData.empresa}" para esta asignación.`);
        setLoading(false);
        return;
      }

      const productos = (data.detallesCobro || []).flatMap((detalle) => {
        
        if (
          detalle.item.includes("Agua") ||
          detalle.item.includes("Electricidad")
        ) {
          return [];
        }

        return [
          {
            docCobro: detalle.docCobro,
            item: detalle.servicios === "arriendo" ? `Arriendo - ${detalle.item}` : detalle.item,
            total: parseFloat(
              (
                // (detalle.item.includes("Agua") || detalle.item.includes("Electricidad")) &&
                // parseFloat(detalle.consumo) > 0
                //   ? parseFloat(detalle.totalClp) / parseFloat(detalle.consumo)
                //   :
                parseFloat(detalle.totalClp)
              ).toFixed(2)
            ),
            cantidad:
              // detalle.item.includes("Agua") || detalle.item.includes("Electricidad")
              //   ? detalle.consumo
              //   :
              1,
            unidadMedida: detalle.unidadMedida,
            lectInicial: detalle.lectInicial,
            lectFinal: detalle.lectFinal,
            consolidado: detalle.consolidado,
            agrupamiento: detalle.agrupamiento,
          },
        ];
      });

      //generar documento de payload
      const documento = {
        productos
      };

      const payload = [
        {
          descripcionContrato: rowData.folio ? `${rowData.folio}` : `${rowData.idCotizacion}`,
          asignacion: `${rowData.idCotizacion}`,
          rut: empresaAsignacion.rut,
          rutFuncionario: empresaAsignacion.rut,
          empresa: rowData.empresa,
          documento: documento,
          periodo: moment(periodoDeCobro).format("YYYY-MM"),
          periodoLetras: periodoLetras,
          fechaEmision: moment(primerDiaHabil).format("DD-MM-YYYY"),
          fechaVencimiento: moment(fechaVencimientoHabil).format("DD-MM-YYYY"),
          valorUf: ufTipoCambio,
          fechaValorUF: moment(primerDiaHabil).format("DD-MM-YYYY"), 
          rutContraparte: rowData.cliente.rut,
          razonSocialContraparte: rowData.cliente.razonSocial?.trim() ?? null,
          aliasContraparte: rowData.cliente.alias?.trim() ?? null,
          giroContraparte: rowData.cliente.giro,
          comunaContraparte: rowData.domicilios[0]?.comuna
            ? rowData.domicilios[0].comuna
            : "No tiene comuna",
          codigoSucursal: 1,
          primer_mes: rowData.primer_mes,
        },
      ];

      //console.log("Payload a enviar:", JSON.stringify(payload, null, 2));

      const response = await guardarDetalleDocumento(urlDocumentos, payload);

      if (response.ok) {
        // Marcar todas las asignaciones como enviadas
        let marcarEnvioSuccess = true;
        try {
          const marcarEnvioResult = await marcarEnvioAsignacion(
            baseUrl,
            rowData.id,
            sessionStorage.getItem("usuario_id")
          );
          if (!marcarEnvioResult.respuesta) {
            marcarEnvioSuccess = false;
          }
        } catch (error) {
          marcarEnvioSuccess = false;
        }
        if (marcarEnvioSuccess) {
          alert("Data insertada correctamente en facturador.");
        } else {
          alert(
            "Data insertada correctamente en facturador pero no pudo ser marcada en bodegas."
          );
        }
        
      } else {
        const errorMessage = await response.text();
        alert(`Error inserting data: ${errorMessage}`);
        throw new Error(`Failed to send data: ${response.status}`);
      }
    } catch (error) {
      console.error("Error sending data to system:", error);

    } finally {
      setLoading(false);
      handleFetchAllData();
    }
  };

  const [mostrarEnUf, setMostrarEnUf] = useState(true);

  return (
    <div>
       {/* BOTONES DE CABECERA */}
        <div className="p-col mt-3" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <div className="p-input-icon-left" style={{ flex: "1", marginRight: "1rem" }}>
            <i className="pi pi-search" />
            <InputText 
              placeholder="Buscar por Cliente..." 
              style={{ width: "50%"}}
              value={globalFilter ? globalFilter.toUpperCase() : ""}
              onChange={(e) => setGlobalFilter(e.target.value.toUpperCase())}
            />
          </div>
          <div style={{ display: "flex", gap: "1rem" }}>
            <Button
              label={mostrarEnUf ? "Mostrar en CLP" : "Mostrar en UF"}
              icon="pi pi-exchange"
              className="p-button p-button-rounded"
              onClick={() => setMostrarEnUf(!mostrarEnUf)}
              disabled={loading || isMonedaServiceUnavailable}
              raised
              severity="info"
            />
            <Button
              label="Sincronizar"
              icon={loading ? "pi pi-spinner" : "pi pi-refresh"}
              className="p-button p-button-rounded"
              onClick={() => handleSincronizar()}
              disabled={loading || isMonedaServiceUnavailable}
              raised
            />
          </div>
        </div>

        {/* TABLA Cierre de periodo */}
        <div className="col-12">
          <DataTable
            header={`Se encontraron ${asignaciones.length} asignaciones`}
            showGridlines
            key={tableKey}
            value={asignaciones}
            paginator
            first={first}
            rows={20}
            rowsPerPageOptions={[20, 50, 100, 150]}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
            currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} asignaciones"
            dataKey="id"
            filters={filters}
            filterDisplay="menu"
            loading={loading}
            scrollable
            emptyMessage="No se encontraron registros."
            rowClassName="row-clickable"
            className="p-datatable-gridlines"
            onRowClick={(e) => handleOpenDialog(e.data)}
            globalFilter={globalFilter}
            stripedRows="true"
            scrollHeight="57vh"
          >
            <Column
              field="id"
              header="Asignación"
              style={{ minWidth: "7rem" }}
              body={(rowData) => (
                <span>
                  <strong>Folio</strong> {rowData.folio}
                  {esAsignacionPadre(rowData) && (
                    <span
                      style={{
                        marginLeft: "0.5rem",
                        padding: "0.1rem 0.4rem",
                        borderRadius: "3px",
                        backgroundColor: "rgba(158, 158, 158, 0.2)",
                        color: "#616161",
                        fontSize: "0.7rem",
                        fontWeight: "bold",
                      }}
                    >
                      HISTÓRICO
                    </span>
                  )}
                  <br/>
                  <strong>ID</strong> {rowData.idCotizacion}
                </span>
              )}
            />

            <Column
              field="cliente.alias"
              header="Cliente Alias"
              style={{ minWidth: "7rem" }}
              body={(rowData) => rowData.cliente.alias}
              sortable
            />

            <Column
              field="empresa"
              header="Empresa"
              style={{ minWidth: "7rem" }}
              body={(rowData) => rowData.empresa}
              sortable
            />

            <Column
              header="Metodo de pago"
              body={() => <span>Facturas separadas</span>}
            />

            <Column
              header="Arriendo"
              style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
              body={(rowData) => {
                if (!tieneValorUf) return mostrarEnUf ? "UF 0,00" : "$ 0";
                const clp = rowData.detalles.arriendo_clp || 0;
                const uf = rowData.detalles.arriendo_uf || 0;
                const total = mostrarEnUf
                  ? uf + clp / ufTipoCambio
                  : clp + uf * ufTipoCambio;
                return mostrarEnUf
                ? `UF ${formatNumber(total)}`
                : `$ ${Math.round(total).toLocaleString("es-CL")}`;
              }}
            />

            <Column
              header="Gasto Común"
              style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
              body={(rowData) => {
                if (!tieneValorUf) return mostrarEnUf ? "UF 0,00" : "$ 0";
                const clp = rowData.detalles.gasto_comun_clp || 0;
                const uf = rowData.detalles.gasto_comun_uf || 0;
                const valorUf = ufTipoCambio || 1;
                const total = mostrarEnUf
                  ? uf + clp / valorUf
                  : clp + uf * valorUf;
                return mostrarEnUf
                ? `UF ${formatNumber(total)}`
                : `$ ${Math.round(total).toLocaleString("es-CL")}`;
              }}
            />
            <Column
              header="Servicio"
              style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
              body={(rowData) => {
                if (!tieneValorUf) return mostrarEnUf ? "UF 0,00" : "$ 0";
                const clpServicio = rowData.detalles.servicio_clp || 0;
                const ufServicio = rowData.detalles.servicio_uf || 0;
                const clpInternet = rowData.detalles.internet_clp || 0;
                const ufInternet = rowData.detalles.internet_uf || 0;
                const valorUf = ufTipoCambio || 1;
                const total = mostrarEnUf
                  ? ufServicio +
                    ufInternet +
                    (clpServicio + clpInternet) / valorUf
                  : clpServicio +
                    clpInternet +
                    (ufServicio + ufInternet) * valorUf;
                return mostrarEnUf
                ? `UF ${formatNumber(total)}`
                : `$ ${Math.round(total).toLocaleString("es-CL")}`;
              }}
            />
            <Column
              header="Obra Civil"
              style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
              body={(rowData) => {
                if (!tieneValorUf) return mostrarEnUf ? "UF 0,00" : "$ 0";
                const clp = rowData.detalles.obra_clp || 0;
                const uf = rowData.detalles.obra_uf || 0;
                const valorUf = ufTipoCambio || 1;
                const total = mostrarEnUf
                  ? uf + clp / valorUf
                  : clp + uf * valorUf;
                return mostrarEnUf
                ? `UF ${formatNumber(total)}`
                : `$ ${Math.round(total).toLocaleString("es-CL")}`;
              }}
            />
            <Column
              header="Total"
              style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
              body={(rowData) => {
                if (!tieneValorUf) return mostrarEnUf ? "UF 0,00" : "$ 0";
                const valorUf = ufTipoCambio || 1;
                const arriendoClp = rowData.detalles.arriendo_clp || 0;
                const arriendoUf = rowData.detalles.arriendo_uf || 0;
                const gastoClp = rowData.detalles.gasto_comun_clp || 0;
                const gastoUf = rowData.detalles.gasto_comun_uf || 0;
                const servicioClp = rowData.detalles.servicio_clp || 0;
                const servicioUf = rowData.detalles.servicio_uf || 0;
                const internetClp = rowData.detalles.internet_clp || 0;
                const internetUf = rowData.detalles.internet_uf || 0;
                const obraClp = rowData.detalles.obra_clp || 0;
                const obraUf = rowData.detalles.obra_uf || 0;

                const total = mostrarEnUf
                  ? (arriendoClp +
                      gastoClp +
                      servicioClp +
                      internetClp +
                      obraClp) /
                      valorUf +
                    arriendoUf +
                    gastoUf +
                    servicioUf +
                    internetUf +
                    obraUf
                  : (arriendoUf + gastoUf + servicioUf + internetUf + obraUf) *
                      valorUf +
                    arriendoClp +
                    gastoClp +
                    servicioClp +
                    internetClp +
                    obraClp;

                    return mostrarEnUf
                    ? `UF ${formatNumber(total)}`
                    : `$ ${Math.round(total).toLocaleString("es-CL")}`;
                  }}
            />

            <Column
              header="Cierre"
              body={actionBodyTemplate}
            />
            <Column header="Reproceso" body={buttonTemplate}/>

            <Column
              header="Estado"
              body={(rowData) => {
                const esPadre = esAsignacionPadre(rowData);
                // Sin ejecucion del cierre: el envio queda bloqueado hasta ejecutar el cierre
                const sinEjecutar = !esPadre && !rowData.enviado && !rowData.ejecutado;
                // Suma simple de todos los subtotales: si es 0, deshabilitamos el botón
                const isZeroTotal =
                  [
                    rowData.detalles?.arriendo_clp,
                    rowData.detalles?.arriendo_uf,
                    rowData.detalles?.gasto_comun_clp,
                    rowData.detalles?.gasto_comun_uf,
                    rowData.detalles?.servicio_clp,
                    rowData.detalles?.servicio_uf,
                    rowData.detalles?.internet_clp,
                    rowData.detalles?.internet_uf,
                    rowData.detalles?.obra_clp,
                    rowData.detalles?.obra_uf,
                  ].reduce((sum, v) => sum + (Number(v) || 0), 0) === 0;

                return (
                  <Button
                    label={esPadre ? "Histórico" : rowData.enviado ? "Enviado" : sinEjecutar ? "Sin Ejecutar" : "Por Enviar"}
                    icon={`pi ${esPadre ? 'pi-lock' : rowData.enviado ? 'pi-check-circle' : sinEjecutar ? 'pi-ban' : 'pi-clock'}`}
                    className={`p-button-rounded p-button ${esPadre ? 'p-button-secondary' : rowData.enviado ? 'p-button-success' : sinEjecutar ? 'p-button-secondary' : 'p-button-warning'}`}
                    onClick={() => sendDataToSystem(rowData)}
                    disabled={loading || esPadre || sinEjecutar || isZeroTotal || isMonedaServiceUnavailable}
                    tooltip={
                      esPadre
                        ? 'Asignación histórica (padre): solo referencia'
                        : rowData.enviado
                          ? `Enviado el ${moment(rowData.fechaEnvio).format('DD/MM/YYYY HH:mm')}`
                          : sinEjecutar
                            ? 'Cierre no ejecutado: ejecute el cierre para habilitar el envío'
                            : isMonedaServiceUnavailable
                              ? 'No disponible: servicio de monedas del período'
                              : ''
                    }
                    tooltipOptions={{ position: 'top' }}
                    style={{
                      minWidth: '120px',
                      transition: 'all 0.3s ease',
                      backgroundColor: esPadre ? 'rgba(158, 158, 158, 0.2)' : rowData.enviado ? 'rgba(76, 175, 80, 0.1)' : sinEjecutar ? 'rgba(239, 83, 80, 0.1)' : 'rgba(255, 167, 38, 0.1)',
                      color: esPadre ? '#616161' : rowData.enviado ? '#2E7D32' : sinEjecutar ? '#C62828' : '#E65100',
                      border: `1px solid ${esPadre ? '#9E9E9E' : rowData.enviado ? '#4CAF50' : sinEjecutar ? '#E57373' : '#FFA726'}`,
                    }}
                  />
                );
              }}
              style={{ 
                minWidth: "12rem", 
                textAlign: "center"
              }}
            />

          </DataTable>
        </div>
      
      <DetallePeriodoCobro
        displayBasic={displayBasic}
        setDisplayBasic={setDisplayBasic}
        basicDialogFooter={basicDialogFooter}
        modalLoading={modalLoading}
        error={error}
        fila={fila}
        arriendoDescriptions={arriendoDescriptions}
        periodoDeCobro={periodoDeCobro}
        fechaDeEmision={fechaDeEmision}
        vencimiento={vencimiento}
        summary={summary}
        handleCopyDetails={handleCopyDetails}
        ufTipoCambio={ufTipoCambio}
        arriendoItems={arriendoItems}
        gastoComunItems={gastoComunItems}
        serviciosItems={serviciosItems}
        obrasCivilesItems={obrasCivilesItems}
        exportToPDF={exportToPDF}
        exportLoading={exportLoading}
      />
    </div>
  );
};

export default CierreTable;
